export interface SignalWait {
	promise: Promise<void>;
	/** Remove os listeners e cancela o fallback sem resolver a promise. */
	cancel: () => void;
}

/**
 * Resolve no primeiro entre: um dos `eventNames`, um dos `gestureTypes`, ou o
 * fallback de `fallbackMs` — sempre com limpeza dos listeners. Primitivo
 * compartilhado por todo gate "espera sinal OU gesto OU timeout" da feature de
 * shorts (janela de vizinhos, warm do modal de contato) — extrair evita
 * reimplementar a mesma limpeza de listeners em cada site.
 *
 * `fallbackGuard`, se passado, condiciona so o disparo por TIMEOUT: se
 * retornar false o fallback e reagendado em vez de resolver. Sinal real e
 * gesto sempre resolvem na hora — sao intencao explicita, nunca um "cego".
 */
export function waitForFirstSignal(
	eventNames: readonly string[],
	fallbackMs: number,
	gestureTypes: readonly string[] = [],
	fallbackGuard?: () => boolean
): SignalWait {
	let settled = false;
	let timer: ReturnType<typeof setTimeout> | null = null;
	let resolvePromise: () => void = () => {};

	const promise = new Promise<void>(resolve => {
		resolvePromise = resolve;
	});

	const cancel = () => {
		if (timer !== null) clearTimeout(timer);
		timer = null;
		for (const type of eventNames) window.removeEventListener(type, settle);
		for (const type of gestureTypes) window.removeEventListener(type, settle);
	};

	function settle() {
		if (settled) return;
		settled = true;
		cancel();
		resolvePromise();
	}

	function onFallbackDue() {
		if (fallbackGuard && !fallbackGuard()) {
			timer = setTimeout(onFallbackDue, fallbackMs);
			return;
		}
		settle();
	}

	for (const type of eventNames) {
		window.addEventListener(type, settle, { once: true });
	}
	for (const type of gestureTypes) {
		window.addEventListener(type, settle, { once: true, passive: true });
	}
	timer = setTimeout(onFallbackDue, fallbackMs);

	return { promise, cancel };
}
